/**
 * Rows the operator settled from this page, kept on screen until it reloads
 * (CAP-785).
 *
 * Settling a row used to make it vanish at once, leaving a twelve-second toast
 * as the only trace - easy to miss when the click landed on the wrong row. A
 * settled row now stays where it was, greyed, saying what was done.
 *
 * Held in component state only: a reload, or leaving the page, clears them.
 * They never count toward a total, which stays admin-api's.
 */
export interface SettledRow<T> {
  /** `jobKey` of the row. */
  key: string
  /** The row as it stood when it was acted on. */
  item: T
  /** What was done, as the toast said it: "Ack recorded". */
  describe: string
  /** The property, day and crew, as the toast said it. */
  context: string
  /** The filter view it was settled in; other views do not show it. */
  view: string
  /** Key of the row shown just above it at the time; null when it was first. */
  after: string | null
  /** Its position at the time, for when `after` is no longer on screen. */
  index: number
}

export interface DisplayRow<T> {
  item: T
  /** Present when the row is a settled one, kept for the record. */
  settled?: SettledRow<T>
}

/**
 * The live rows with the settled ones put back where they were.
 *
 * A row admin-api still returns is shown live, never as settled: it has
 * something left to do (one reason of two, or an undo put it back), and that
 * is what the operator needs to see. Each settled row goes back under the row
 * that was above it, which may itself be a settled row, so placing is
 * repeated until nothing more can be anchored; anything left falls back to
 * its old position.
 */
export function withSettledRows<T>(
  live: readonly T[],
  settled: readonly SettledRow<T>[],
  keyOf: (item: T) => string
): DisplayRow<T>[] {
  const rows: DisplayRow<T>[] = live.map(item => ({ item }))
  const liveKeys = new Set(live.map(keyOf))
  let pending = settled.filter(row => !liveKeys.has(row.key))

  const position = (key: string) => rows.findIndex(row => keyOf(row.item) === key)

  let placed = true
  while (pending.length > 0 && placed) {
    placed = false
    const next: SettledRow<T>[] = []
    for (const row of pending) {
      const at = row.after === null ? 0 : position(row.after) + 1
      if (row.after !== null && at === 0) {
        next.push(row)
        continue
      }
      rows.splice(at, 0, { item: row.item, settled: row })
      placed = true
    }
    pending = next
  }
  for (const row of pending) {
    rows.splice(Math.min(row.index, rows.length), 0, { item: row.item, settled: row })
  }
  return rows
}
